type Wave = OscillatorType;

const MUSIC_NOTES = [
  659, 659, 0, 659, 0, 523, 659, 0,
  784, 0, 0, 0, 392, 0, 0, 0,
  523, 0, 392, 0, 330, 0, 440, 494,
  466, 440, 392, 659, 784, 880, 698, 784,
];

export type RetroSoundEvent =
  | 'boot'
  | 'power'
  | 'box-open'
  | 'pickup-cartridge'
  | 'drop-cartridge'
  | 'insert-cartridge'
  | 'button'
  | 'jump'
  | 'coin'
  | 'block'
  | 'stomp'
  | 'damage'
  | 'death'
  | 'pause'
  | 'complete';

export class RetroAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private muted = false;

  async unlock(): Promise<void> {
    if (!this.context) {
      const Context = window.AudioContext
        ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Context) return;
      this.context = new Context();
      this.master = this.context.createGain();
      this.musicGain = this.context.createGain();
      this.sfxGain = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.72;
      this.musicGain.gain.value = 0.16;
      this.sfxGain.gain.value = 0.42;
      this.musicGain.connect(this.master);
      this.sfxGain.connect(this.master);
      this.master.connect(this.context.destination);
    }
    if (this.context.state !== 'running') await this.context.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (!this.context || !this.master) return;
    this.master.gain.setTargetAtTime(muted ? 0 : 0.72, this.context.currentTime, 0.02);
  }

  isMuted(): boolean {
    return this.muted;
  }

  play(event: RetroSoundEvent): void {
    if (!this.context || !this.sfxGain || this.context.state !== 'running') return;
    switch (event) {
      case 'boot':
        this.sequence([392, 523, 659, 784], 0.07, 'square', 0.15);
        break;
      case 'power':
        this.tone(110, 0.06, 'square', 0.11, 74);
        break;
      case 'box-open':
        this.sequence([155, 196, 247], 0.055, 'triangle', 0.08);
        break;
      case 'pickup-cartridge':
        this.sequence([330, 440], 0.045, 'square', 0.08);
        break;
      case 'drop-cartridge':
        this.tone(98, 0.07, 'square', 0.08, 76);
        break;
      case 'insert-cartridge':
        this.sequence([131, 196, 392, 784], 0.045, 'square', 0.13);
        break;
      case 'button':
        this.tone(240, 0.025, 'square', 0.045, 190);
        break;
      case 'jump':
        this.tone(260, 0.14, 'square', 0.13, 620);
        break;
      case 'coin':
        this.sequence([988, 1319], 0.045, 'square', 0.14);
        break;
      case 'block':
        this.sequence([165, 110], 0.035, 'square', 0.1);
        break;
      case 'stomp':
        this.tone(150, 0.08, 'square', 0.13, 88);
        break;
      case 'damage':
        this.sequence([330, 247, 196], 0.055, 'sawtooth', 0.1);
        break;
      case 'death':
        this.sequence([494, 466, 440, 392, 330, 262], 0.11, 'square', 0.13);
        break;
      case 'pause':
        this.sequence([392, 0, 392], 0.06, 'square', 0.08);
        break;
      case 'complete':
        this.sequence([523, 659, 784, 1047, 784, 1047], 0.08, 'square', 0.15);
        break;
    }
  }

  startMusic(): void {
    if (this.musicTimer !== null || !this.context || !this.musicGain) return;
    this.musicStep = 0;
    const tick = () => {
      if (!this.context || !this.musicGain) return;
      const note = MUSIC_NOTES[this.musicStep % MUSIC_NOTES.length];
      if (note > 0) this.tone(note, 0.085, 'square', 0.045, note, this.musicGain);
      if (this.musicStep % 4 === 0) {
        const bass = MUSIC_NOTES[(this.musicStep + 16) % MUSIC_NOTES.length] || 196;
        this.tone(bass / 2, 0.11, 'triangle', 0.035, bass / 2, this.musicGain);
      }
      this.musicStep += 1;
    };
    tick();
    this.musicTimer = window.setInterval(tick, 115);
  }

  stopMusic(): void {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
  }

  dispose(): void {
    this.stopMusic();
    void this.context?.close();
    this.context = null;
    this.master = null;
    this.musicGain = null;
    this.sfxGain = null;
  }

  private sequence(notes: number[], spacing: number, wave: Wave, volume: number): void {
    if (!this.context || !this.sfxGain) return;
    notes.forEach((note, index) => {
      if (note > 0) this.tone(note, spacing * 0.85, wave, volume, note, this.sfxGain, index * spacing);
    });
  }

  private tone(
    startFrequency: number,
    duration: number,
    wave: Wave,
    volume: number,
    endFrequency = startFrequency,
    output: AudioNode | null = this.sfxGain,
    delay = 0,
  ): void {
    if (!this.context || !output) return;
    const start = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = wave;
    oscillator.frequency.setValueAtTime(Math.max(30, startFrequency), start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, endFrequency), start + duration);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(output);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }
}
